import { useTranslation } from "react-i18next";
import Button from "../Button/Button";
import Modal, { ModalProps } from "../Modal/Modal";
import RadioButtonGroup from "../Input/RadioButtonGroup";
import { useState } from "react";
import Textfield from "../Input/Textfield";
import { useForm } from "react-hook-form";
import { useModalMeta, useSetModal } from "@/shared/atoms/useModal";
import { errorToast, formatPrice } from "@/shared/utils/helpers";
import useOrderRefund from "@/shared/hooks/useOrderRefund";
import { AppsmenError } from "@/shared/utils/interfaces";
import usePartialRefundSessionMutation from "@/shared/mutations/orderSessions/usePartialRefundSessionMutation";
import useFullRefundSessionMutation from "@/shared/mutations/orderSessions/useFullRefundSessionMutation";

const RefundModal = ({ onExit, ...props }: ModalProps) => {
  const { t } = useTranslation("");
  // `onRefund` replaces the API refund, e.g. to pay the amount out through the CashDro.
  const [{ total, id, refetch, isSession, title = "refund", onRefund }] =
    useModalMeta();
  const setModal = useSetModal();
  const [fullRefund, setFullRefund] = useState(true);
  const { register, handleSubmit, errors, setValue, clearErrors } = useForm();
  const { orderRefundMutation, orderPartialRefundMutation } = useOrderRefund();
  const sessionRefundMutation = useFullRefundSessionMutation();
  const sessionPartialRefundMutation = usePartialRefundSessionMutation();

  const onSubmit = ({ refundAmount }) => {
    if (onRefund) {
      setModal("", {});
      onRefund(parseFloat(fullRefund ? total : refundAmount));
      return;
    }

    if (fullRefund) {
      const mutation = isSession ? sessionRefundMutation : orderRefundMutation;

      mutation.mutateAsync(
        { id },
        {
          onSuccess: () => {
            setModal("", {});
            refetch();
          },
        }
      );
    } else {
      const mutation = isSession
        ? sessionPartialRefundMutation
        : orderPartialRefundMutation;

      mutation.mutateAsync(
        { id, refundAmount },
        {
          onSuccess: () => {
            setModal("", {});
            refetch();
          },
          onError: (error: AppsmenError) => {
            errorToast(error?.response?.data?.error_message);
          },
        }
      );
    }
  };

  return (
    <Modal width={"58rem"} onExit={onExit} {...props}>
      <RadioButtonGroup
        label={t(title)}
        onChange={(e) => {
          const isFullRefund = Number(e.target.value) === 1;
          setFullRefund(isFullRefund);
          setValue("refundAmount", isFullRefund ? total : "");
          clearErrors("refundAmount");
        }}
        value={fullRefund ? 1 : 2}
        options={[
          { value: 1, label: t("fullRefund", { value: 1 }) },
          { value: 2, label: t("partialRefund", { value: 2 }) },
        ]}
      />

      <div>{t("totalAmountRefundable", { value: formatPrice(total) })}</div>

      <form id="refund" onSubmit={handleSubmit(onSubmit)} className="mb-[1rem]">
        <Textfield
          type="number"
          name="refundAmount"
          placeholder={t("refundAmount")}
          disabled={fullRefund}
          defaultValue={total}
          ref={register({
            required: {
              value: true,
              message: t("fieldRequired"),
            },
            validate: (refundAmount) => {
              if (parseFloat(refundAmount) > parseFloat(total)) {
                return t("refundAmountCannotExceedTheTotalAmount");
              }
              return true;
            },
          })}
          step="0.01"
          error={errors?.refundAmount?.message}
        />
      </form>

      <Button
        type="submit"
        form="refund"
        isPending={
          orderRefundMutation.isPending ||
          orderPartialRefundMutation.isPending ||
          sessionRefundMutation.isPending ||
          sessionPartialRefundMutation.isPending
        }
      >
        {t("save")}
      </Button>
    </Modal>
  );
};

export default RefundModal;
